import axios from 'axios';

let _baseURL = 'http://localhost:3000';
let _accessToken: string | null = null;
let _tokenExpiresAt: number | null = null;
let _unauthorizedHandler: (() => void) | null = null;

export interface RefreshResult {
  access_token: string;
  expires_at: number;
  user: { id: string; email: string; role: string };
}

export async function refreshAccessToken(opts?: { timeout?: number }): Promise<RefreshResult> {
  const { data } = await axios.post<RefreshResult>(
    `${_baseURL}/v1/auth/refresh`,
    {},
    { withCredentials: true, timeout: opts?.timeout },
  );
  _accessToken = data.access_token;
  _tokenExpiresAt = data.expires_at;
  return data;
}

export function configureApiClient(opts: { baseURL: string }) {
  _baseURL = opts.baseURL;
  http.defaults.baseURL = opts.baseURL;
}

export function setUnauthorizedHandler(fn: (() => void) | null) {
  _unauthorizedHandler = fn;
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

export function setTokenExpiry(expiresAt: number | null): void {
  _tokenExpiresAt = expiresAt;
}

export function getTokenExpiry(): number | null {
  return _tokenExpiresAt;
}

export const http = axios.create({
  baseURL: _baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── Request interceptor: attach Bearer token ──
http.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// ── Response interceptor: silent refresh on 401 ──
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
  return () => {
    refreshSubscribers = refreshSubscribers.filter((subscriber) => subscriber !== cb);
  };
}

function notifySubscribers(token: string | null) {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  for (const cb of subs) cb(token);
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;

    // Not a 401, or it's the refresh call itself, or already retried
    if (status !== 401 || original.url?.includes('/v1/auth/refresh') || original._retry) {
      return Promise.reject(error);
    }

    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const data = await refreshAccessToken();
        notifySubscribers(data.access_token);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        original._retry = true;
        return http(original);
      } catch {
        _accessToken = null;
        _tokenExpiresAt = null;
        notifySubscribers(null);
        _unauthorizedHandler?.();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Queue this request while refresh is in-flight
    return new Promise((resolve, reject) => {
      let settled = false;
      let unsubscribe: (() => void) | undefined;
      const timeout = setTimeout(() => {
        settled = true;
        unsubscribe?.();
        reject(error);
      }, 10000);

      unsubscribe = subscribeTokenRefresh((newToken: string | null) => {
        if (settled) return;
        settled = true;
        unsubscribe?.();
        clearTimeout(timeout);
        if (newToken === null) {
          reject(error);
          return;
        }
        original.headers.Authorization = `Bearer ${newToken}`;
        original._retry = true;
        resolve(http(original));
      });
    });
  },
);
